import React, { useEffect, useRef } from "react";

/**
 * Стеклянная сфера, наполненная светящейся жидкостью. Уровень = прогресс до
 * следующего звания.
 *
 * Рисуется покадрово на канве, а не картинкой: поверхность — две синусоиды с
 * разной скоростью, поэтому волна не повторяется, а пузыри рождаются на дне и
 * лопаются ровно на поверхности. Статичная картинка этого не даёт, а
 * CSS-градиент не даёт волны вовсе.
 *
 * MIN_FILL: даже при нуле баллов внутри остаётся немного жидкости. Пустой шар
 * читается как выключенный, и человек не видит, что знак вообще живой, — а
 * именно на нуле его и надо позвать что-то исправить.
 */
const MIN_FILL = 0.12;

/** Каждому счёту свой цвет: три одинаковых шара в ряд неразличимы. */
export type SphereTint = { deep: number[]; mid: number[]; light: number[] };
export const TINTS: Record<string, SphereTint> = {
    // вклад — аметист
    contrib: { deep: [59, 30, 122], mid: [139, 92, 246], light: [221, 214, 254] },
    // тренировки — красный: усилие, пульс, тело
    workouts: { deep: [127, 29, 29], mid: [239, 68, 68], light: [254, 202, 202] },
    // общественное — синий: люди, встречи, общее дело
    social: { deep: [30, 58, 138], mid: [59, 130, 246], light: [191, 219, 254] },
    // приглашённые — зелёный: рост, новые люди в проекте
    referrals: { deep: [6, 78, 59], mid: [16, 185, 129], light: [167, 243, 208] },
    // обучение — янтарь: тёплый свет знания, и единственный тёплый тон в ряду
    learning: { deep: [120, 53, 15], mid: [245, 158, 11], light: [253, 230, 138] },
};

const rgba = (c: number[], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;

type Bubble = { x: number; y: number; r: number; sp: number; w: number };

export const LiquidSphere = ({
    size,
    fill,
    celebrate = false,
    tint = "contrib",
    className = "",
}: {
    /** Диаметр в CSS-пикселях. */
    size: number;
    /** 0..1 — доля до следующего звания. */
    fill: number;
    /** Всплеск при начислении: волна сильнее, со дна срывается сноп пузырей. */
    celebrate?: boolean;
    /** Ключ из TINTS: разный цвет на каждый счёт. */
    tint?: keyof typeof TINTS | string;
    className?: string;
}) => {
    const ref = useRef<HTMLCanvasElement>(null);
    // Пропсы читаются из ref: цикл rAF живёт дольше одного рендера, и
    // пересоздавать его на каждое изменение fill было бы и дорого, и рвано.
    const state = useRef({ fill, shown: fill, shake: 0, bubbles: [] as Bubble[] });

    useEffect(() => {
        state.current.fill = fill;
    }, [fill]);

    useEffect(() => {
        if (!celebrate) return;
        const s = state.current;
        s.shake = 1;
        for (let i = 0; i < 14; i++) {
            s.bubbles.push({
                x: (Math.random() - 0.5) * 0.7,
                y: 1,
                r: 0.4 + Math.random() * 1.4,
                sp: 0.004 + Math.random() * 0.01,
                w: Math.random() * 6,
            });
        }
    }, [celebrate]);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const T = TINTS[tint] || TINTS.contrib;
        const DEEP = T.deep;
        const MID = T.mid;
        const LIGHT = T.light;
        const S = size;
        const cx = S / 2;
        const cy = S / 2;
        const R = S / 2 - 1;
        const phase = Math.random() * 10;
        let raf = 0;

        const draw = (t: number) => {
            const s = state.current;
            s.shown += (s.fill - s.shown) * 0.09;
            s.shake *= 0.94;

            const shown = MIN_FILL + (1 - MIN_FILL) * Math.max(0, Math.min(1, s.shown));
            const level = cy + R - shown * R * 2;
            const amp = reduce ? 0 : (R * 0.035 + R * 0.09 * s.shake);

            ctx.clearRect(0, 0, S, S);

            // Наружного свечения нет намеренно. Оно рисовалось радиусом 1.7R в
            // канве шириной 2R, круг не помещался и обрезался ПРЯМОУГОЛЬНИКОМ
            // канвы — в углах оставался тон сферы на ~10% альфы, и за каждым
            // шаром стоял цветной квадрат. Уместить ореол некуда: сфера
            // занимает канву целиком. Объём теперь добирается изнутри, где
            // выйти за круг нечему.

            ctx.save();
            ctx.beginPath();
            ctx.arc(cx, cy, R, 0, 7);
            ctx.clip();

            // Пустое стекло.
            const inner = ctx.createRadialGradient(cx - R * 0.3, cy - R * 0.4, R * 0.1, cx, cy, R);
            inner.addColorStop(0, "rgba(255,255,255,.07)");
            inner.addColorStop(0.55, "rgba(20,16,30,.92)");
            inner.addColorStop(1, "rgba(8,6,12,1)");
            ctx.fillStyle = inner;
            ctx.fillRect(cx - R, cy - R, R * 2, R * 2);

            // Жидкость с живой волной.
            ctx.beginPath();
            ctx.moveTo(cx - R, S);
            ctx.lineTo(cx - R, level);
            const stepPx = Math.max(2, R / 24);
            for (let x = -R; x <= R; x += stepPx) {
                const y =
                    level +
                    Math.sin((x / R) * 3.1 + t * 0.0016 + phase) * amp +
                    Math.sin((x / R) * 6.7 - t * 0.0011) * amp * 0.5;
                ctx.lineTo(cx + x, y);
            }
            ctx.lineTo(cx + R, S);
            ctx.closePath();
            const lg = ctx.createLinearGradient(0, level - R * 0.1, 0, cy + R);
            lg.addColorStop(0, rgba(LIGHT, 0.95));
            lg.addColorStop(0.18, rgba(MID, 0.92));
            lg.addColorStop(1, rgba(DEEP, 0.95));
            ctx.fillStyle = lg;
            ctx.fill();

            // Светлая кромка: у поверхности слой тонкий и свет проходит насквозь.
            ctx.save();
            ctx.clip();
            const edge = ctx.createLinearGradient(0, level - R * 0.14, 0, level + R * 0.24);
            edge.addColorStop(0, rgba(LIGHT, 0.85));
            edge.addColorStop(1, rgba(LIGHT, 0));
            ctx.fillStyle = edge;
            ctx.fillRect(cx - R, level - R * 0.14, R * 2, R * 0.4);
            ctx.restore();

            // Пузыри. На мелком размере они превращаются в кашу — не рисуем.
            if (!reduce && S >= 40) {
                if (Math.random() < 0.045 + 0.12 * s.shake && s.bubbles.length < 22) {
                    s.bubbles.push({
                        x: (Math.random() - 0.5) * 1.3,
                        y: 1,
                        r: 0.35 + Math.random() * 1.2,
                        sp: 0.003 + Math.random() * 0.008,
                        w: Math.random() * 6,
                    });
                }
                ctx.save();
                ctx.clip();
                for (let i = s.bubbles.length - 1; i >= 0; i--) {
                    const b = s.bubbles[i];
                    b.y -= b.sp;
                    b.w += 0.05;
                    const by = cy + R - b.y * R * 2;
                    if (by < level + 2) {
                        s.bubbles.splice(i, 1);
                        continue;
                    }
                    const bx = cx + b.x * R * 0.5 + Math.sin(b.w) * R * 0.03;
                    const br = b.r * (R / 60);
                    ctx.beginPath();
                    ctx.arc(bx, by, br, 0, 7);
                    ctx.fillStyle = rgba(LIGHT, 0.3);
                    ctx.fill();
                    ctx.beginPath();
                    ctx.arc(bx - br * 0.3, by - br * 0.3, br * 0.42, 0, 7);
                    ctx.fillStyle = "rgba(255,255,255,.55)";
                    ctx.fill();
                }
                ctx.restore();
            }

            // Стекло: три слоя. Без нижнего ободка шар выглядит наклейкой.
            const spec = ctx.createRadialGradient(
                cx - R * 0.34, cy - R * 0.48, 1, cx - R * 0.34, cy - R * 0.48, R * 0.72);
            spec.addColorStop(0, "rgba(255,255,255,.42)");
            spec.addColorStop(1, "rgba(255,255,255,0)");
            ctx.fillStyle = spec;
            ctx.fillRect(cx - R, cy - R, R * 2, R * 2);

            ctx.beginPath();
            ctx.ellipse(cx - R * 0.36, cy - R * 0.55, R * 0.2, R * 0.1, -0.5, 0, 7);
            ctx.fillStyle = "rgba(255,255,255,.75)";
            ctx.fill();

            // Ободок взят ярче прежнего: он остался единственным источником
            // свечения после снятия наружного ореола.
            const rim = ctx.createRadialGradient(cx, cy, R * 0.62, cx, cy, R);
            rim.addColorStop(0, "rgba(255,255,255,0)");
            rim.addColorStop(0.82, rgba(LIGHT, 0.18));
            rim.addColorStop(1, rgba(LIGHT, 0.55));
            ctx.fillStyle = rim;
            ctx.fillRect(cx - R, cy - R, R * 2, R * 2);

            const bounce = ctx.createRadialGradient(
                cx + R * 0.42, cy + R * 0.55, 1, cx + R * 0.42, cy + R * 0.55, R * 0.6);
            bounce.addColorStop(0, rgba(LIGHT, 0.28));
            bounce.addColorStop(1, rgba(LIGHT, 0));
            ctx.fillStyle = bounce;
            ctx.fillRect(cx - R, cy - R, R * 2, R * 2);
            ctx.restore();

            ctx.beginPath();
            ctx.arc(cx, cy, R, 0, 7);
            ctx.strokeStyle = rgba(LIGHT, 0.42);
            ctx.lineWidth = Math.max(1, R / 60);
            ctx.stroke();

            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(raf);
    }, [size, tint]);

    return (
        <canvas
            ref={ref}
            className={className}
            style={{ width: size, height: size, display: "block" }}
            aria-hidden="true"
        />
    );
};
